import React from 'react';
import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

//- ui
import FMPSectionHeader from '../ui/FMPSectionHeader';

//- form
import FMPSelect from '../pureForm/FMPSelect';
import FMPText from '../pureForm/FMPText';
import FMPDatePicker from '../pureForm/FMPDatePicker';
import FMPTerminalSupplierSelect from '../pureForm/FMPTerminalSupplierSelect';
import FMPTerminalSelect from '../pureForm/FMPTerminalSelect';
import FMPCarrierSelect from '../pureForm/FMPCarrierSelect';
import FMPSwitch from '../pureForm/FMPSwitch';

import { matchProductToDelivery, matchProductToBulkDelivery } from '../../pages/accounting/general';

import { terminalProducts } from '../../libs/products';

const DEFAULT_TERMINAL_SUPPLIER_VALUE = { terminalId: '', supplierId: '' };

//- This is a list of all documents we support.
//- For station invoices we get some and for bulk invoices we get others
const documents = [
	{
		documentType: 'carrierDeliveryTicket',
		display: 'Carrier Delivery Ticket',
		sort: 100,
		fields: [
			{
				label: 'Carrier',
				name: 'carrierId',
				type: 'carrierSelect',
				isRequired: true,
			},
			{
				label: 'Carrier PO Number',
				name: 'carrierPoNumber',
				type: 'string',
				isRequired: true,
			},
			{
				label: 'Delivery Date',
				name: 'deliveryDate',
				type: 'date',
				isRequired: true,
			},
			{
				label: 'Append To Invoice',
				name: 'appendToInvoice',
				type: 'toggle',
				defaultValue: true,
			},
		],
	},
	{
		documentType: 'carrierShippingOrder',
		display: 'Carrier Shipping Order',
		sort: 110,
		fields: [
			{
				label: 'Carrier',
				name: 'carrierId',
				type: 'carrierSelect',
				isRequired: true,
			},
			{
				label: 'Append To Invoice',
				name: 'appendToInvoice',
				type: 'toggle',
				defaultValue: false,
			},
		],

		//- Bulk
	},
	{
		documentType: `pickupBol`,
		display: `Pickup BOL`,
		sort: 400,
		productType: 'etoh',
		justBulk: true,
		fields: [
			{
				/* Turn on when ready to support more than e85
		type : 'productSelect',
		name: 'productType',
		label: 'Product Type',
		isRequired: true,
	}, {
		*/
				type: 'string',
				name: 'bolNumber',
				label: 'BOL Number',
				isRequired: true,
			},
			{
				label: `Pickup Terminal/Supplier`,
				type: 'terminalSupplierSelect',
				name: 'terminalSupplier',
				productType: 'etoh',
				productDisplay: 'Ethanol', //XXX when ready to support more than ethanol
				isRequired: true,
			},
			{
				type: 'number',
				name: 'netGallons',
				label: 'Net Fuel Gals',
				isRequired: true,
			},
			{
				type: 'string',
				name: 'loadingNumber',
				label: 'Loading Number',
				justRackTransfers: true,
			},
			{
				type: 'toggle',
				name: 'appendToInvoice',
				label: 'Append To Invoice',
				defaultValue: true,
			},
		],
	},
	{
		documentType: `offloadBol`,
		display: `Offload BOL`,
		sort: 410,
		productType: 'etoh',
		justBulk: true,
		fields: [
			{
				/* Turn on when ready to support more than e85
			type : 'productSelect',
			name: 'productType',
			label: 'Product Type',
			isRequired: true,
		}, {
		*/
				type: 'string',
				name: 'bolNumber',
				label: 'BOL Number',
				isRequired: true,
			},
			{
				label: `Offload Terminal`,
				type: 'terminalSelect',
				name: 'terminalId',
				productType: 'etoh',
				productDisplay: 'Ethanol',
				isRequired: true,
			},
			{
				type: 'number',
				name: 'netGallons',
				label: 'Net Fuel Gallons',
				isRequired: true,
			},
			{
				type: 'toggle',
				name: 'appendToInvoice',
				label: 'Append To Invoice',
				defaultValue: true,
			},
		],
	},
	{
		documentType: 'veederReceipt',
		display: 'Veeder Receipt',
		sort: 500,
		fields: [
			{
				label: 'Append To Invoice',
				name: 'appendToInvoice',
				type: 'toggle',
				defaultValue: false,
			},
		],
	},
	{
		documentType: 'other',
		display: 'Other',
		sort: 700,
		fields: [
			{
				label: 'Document Name',
				name: 'documentName',
				type: 'string',
				isRequired: true,
			},
			{
				label: 'Append To Invoice',
				name: 'appendToInvoice',
				type: 'toggle',
				defaultValue: false,
			},
		],
	},
	{
		documentType: 'ignore',
		display: 'Ignore / Duplicate',
		sort: 800,
		fields: [],
	},
];

terminalProducts.forEach(({ display, value }, i) => {
	documents.unshift({
		documentType: `stationDeliveryPickupBol`,
		productType: value,
		display: `${display} BOL`,
		sort: 300 + i,
		justStation: true,
		fields: [
			{
				type: 'string',
				name: 'bolNumber',
				label: 'BOL Number',
				isRequired: true,
			},
			{
				label: `${display} Terminal/Supplier`,
				type: 'terminalSupplierSelect',
				name: 'terminalSupplier',
				productType: value,
				productDisplay: display,
				isRequired: true,
			},
			{
				type: 'number',
				name: 'netGallons',
				label: 'Net Amount Fuel',
				isRequired: true,
			},
			{
				type: 'toggle',
				name: 'appendToInvoice',
				label: 'Append To Invoice',
				defaultValue: true,
			},
		],
	});
});

function _key(documentType, productType) {
	return productType ? `${documentType}_${productType}` : documentType;
}

//- Allows us to find a document by the option value
const documentOptionMap = _.keyBy(documents, (document) => {
	const { documentType, productType } = document;
	return _key(documentType, productType);
});

function getDocument({ documentType, productType, optionValue }) {
	const key = optionValue ? optionValue : _key(documentType, productType);
	return documentOptionMap[key];
}

function getBulkDocumentOptions({ productTypes }) {
	const options = [];

	_.sortBy(documents, 'sort').forEach(({ documentType, productType, display, justStation }) => {
		if (justStation) {
			return;
		}

		const value = _key(documentType, productType);

		if (!productType) {
			options.push({ display, value });
			return;
		}

		if (!_.includes(productTypes, productType)) {
			return;
		}
		options.push({ display, value });
	});
	return options;
}

function getStationDocumentOptions({ productTypes }) {
	const options = [];

	_.sortBy(documents, 'sort').forEach(({ documentType, productType, display, justBulk }) => {
		if (justBulk) {
			return;
		}

		const value = _key(documentType, productType);
		if (!productType) {
			options.push({ display, value });
			return;
		}

		if (!_.includes(productTypes, productType)) {
			return;
		}
		options.push({ display, value });
	});
	return options;
}

/*
//- station options vs bulk
const stationDocumentOptions = [];
const bulkDocumentOptions = [];

_.sortBy(documentTypes, 'sort').forEach(({ display, value, justStation, justBulk}) => {
	if (justStation) {
		stationDocumentOptions.push({ display, value });
	} else if (justBulk) {
		bulkDocumentOptions.push({ display, value });
	} else {
		stationDocumentOptions.push({ display, value });
		bulkDocumentOptions.push({ display, value });
	}
});
 */

export default class Page extends Component {
	constructor(props) {
		super(props);

		const {
			bulkInvoice,
			rackTransfer,
			//stationInvoice,
			stationDelivery,
			documentType = '',
			formFieldError = {},
		} = props;

		//- If we have data we might have the product type as well
		const productType = _.get(this.props.data, 'productType');
		const document = getDocument({ documentType, productType });

		let { fields, data } = this.getDataAndFieldsFromDocument({ document });

		//- Data might be passed in if they reopen the document
		if (props.data) {
			data = _.assign(data, props.data);
		}

		const isBulk = bulkInvoice;
		const isRack = rackTransfer;
		let documentOptions = [];
		let documentOption = _key(documentType, productType) || '';

		if (isBulk) {
			const { productType } = bulkInvoice;
			documentOptions = getBulkDocumentOptions({ productTypes: [productType] });
		} else if (isRack) {
			const { productType } = rackTransfer;
			documentOptions = getBulkDocumentOptions({ productTypes: [productType] });
		} else {
			const { firstTerminalProductType, secondTerminalProductType } = stationDelivery;

			const productTypes = [firstTerminalProductType];
			if (secondTerminalProductType) {
				productTypes.push(secondTerminalProductType);
			}
			documentOptions = getStationDocumentOptions({ productTypes });
		}

		this.state = {
			isBulk,
			isRack,
			documentOption,
			documentOptions,
			formFieldError,
			fields,
			documentType,
			data,
		};
		props.onChange(this.state);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.formFieldError !== this.props.formFieldError) {
			this.setState({ formFieldError: this.props.formFieldError });
		}
	}

	onChange = (value, path) => {
		const arrayPath = _.toPath(path);
		const basePath = _.head(arrayPath);
		let base = this.state[basePath];
		let newState = {};

		if (_.isArray(base) || _.isObject(base)) {
			base = _.cloneDeep(base);
			_.set(base, arrayPath.slice(1), value);
			newState[basePath] = base;
		} else {
			newState[basePath] = value;
		}

		if (basePath === 'documentOption') {
			const document = getDocument({ optionValue: value });
			newState.documentType = document.documentType;
			const { fields, data } = this.getDataAndFieldsFromDocument({ document });
			newState.fields = fields;
			newState.data = data;
		}

		this.setState(newState);
		this.props.onChange(newState);
	};

	onTerminalSupplierChange = (value, path) => {
		const { terminalId, supplierId } = value;
		let newVal = { terminalId, supplierId };
		newVal = _.pickBy(newVal, (v) => {
			return !_.isUndefined(v);
		});

		if (_.keys(newVal).length) {
			const existing = _.get(this.state, path, {});
			newVal = _.assign({}, existing, newVal);
			this.onChange(newVal, path);
		}
	};

	getDataAndFieldsFromDocument = ({ document }) => {
		if (!document) {
			return { fields: [], data: {} };
		}

		const { bulkDelivery } = this.props;
		if (bulkDelivery) {
			return this._getDataForBulk(document);
		} else {
			return this._getDataForStation(document);
		}
	};

	_getDataForBulk = (document) => {
		const { bulkDelivery, bulkInvoice, rackTransfer } = this.props;
		const isRack = rackTransfer;

		const { documentType, fields, productType } = document;
		const data = {};

		if (productType) {
			data.productType = productType;
		}

		const deliveryDefaults = {};
		const deliveryHelpText = {};
		if (documentType === 'carrierDeliveryTicket' || documentType === 'carrierShippingOrder') {
			const { carrierId, deliveryDate } = bulkDelivery;
			deliveryDefaults.carrierId = carrierId;
			deliveryDefaults.deliveryDate = deliveryDate;
		} else if (documentType === 'pickupBol') {
			const { pickupTerminalId, pickupSupplierId, loadingNumber } = bulkDelivery;

			deliveryDefaults.terminalSupplier = {
				terminalId: pickupTerminalId,
				supplierId: pickupSupplierId,
			};
			deliveryDefaults.loadingNumber = loadingNumber;
			deliveryHelpText.loadingNumber = `Expect ${loadingNumber} based on bulk delivery`;

			const matchResult = matchProductToBulkDelivery({
				bulkInvoice,
				rackTransfer,
				netGallons: 0,
			});
			deliveryHelpText.netGallons = matchResult.expectedLine;
		} else if (documentType === 'offloadBol') {
			const { deliveryTerminalId } = bulkDelivery;
			deliveryDefaults.terminalId = deliveryTerminalId;

			const matchResult = matchProductToBulkDelivery({
				bulkInvoice,
				rackTransfer,
				netGallons: 0,
			});
			deliveryHelpText.netGallons = matchResult.expectedLine;
		}

		const finalFields = [];
		//- Fill in the default values. Either from the delivery or really default
		fields.forEach((field) => {
			const { name, defaultValue, justRackTransfers } = field;
			if (isRack && name === 'appendToInvoice') {
				return;
			}

			if (justRackTransfers && !isRack) {
				return;
			}

			if (!_.isUndefined(deliveryDefaults[name])) {
				data[name] = deliveryDefaults[name];
			} else {
				data[name] = defaultValue;
			}

			if (!_.isUndefined(deliveryHelpText[name])) {
				field.helpText = deliveryHelpText[name];
			}

			if (field.productType) {
				field.productTypes = [productType];
			}

			finalFields.push(field);
		});
		return { fields: finalFields, data };
	};

	_getDataForStation = (document) => {
		const { stationDelivery, stationDeliveryMap, stationInvoice } = this.props;

		const { documentType, fields, productType } = document;
		const data = {};

		if (productType) {
			data.productType = productType;
		}

		const deliveryDefaults = {};
		const deliveryHelpText = {};
		if (documentType === 'carrierDeliveryTicket' || documentType === 'carrierShippingOrder') {
			const {
				carrierId,
				allDayWindow,
				deliveryWindowStartDateTime,
				deliveryWindowEndDateTime,
			} = stationDelivery;

			deliveryDefaults.carrierId = carrierId;

			let deliveryDate = moment();
			//- Open the invoice at the beginning of the window
			if (allDayWindow) {
				deliveryDate = moment(deliveryWindowStartDateTime);
			} else if (deliveryWindowStartDateTime) {
				deliveryDate = moment(deliveryWindowStartDateTime);
			} else if (deliveryWindowEndDateTime) {
				deliveryDate = moment(deliveryWindowEndDateTime);
			}

			deliveryDefaults.deliveryDate = deliveryDate.format('YYYY-MM-DD');
		} else if (documentType === 'stationDeliveryPickupBol') {
			//- Find which product type matches which terminal

			const {
				//firstTerminalProductType,
				firstTerminalTerminalId,
				firstTerminalSupplierId,
				firstTerminalBackups,

				secondTerminalProductType,
				secondTerminalTerminalId,
				secondTerminalSupplierId,
				secondTerminalBackups,
			} = stationDelivery;

			let terminalId, supplierId, backups;
			if (secondTerminalProductType === productType) {
				terminalId = secondTerminalTerminalId;
				supplierId = secondTerminalSupplierId;
				backups = secondTerminalBackups;
			} else {
				terminalId = firstTerminalTerminalId;
				supplierId = firstTerminalSupplierId;
				backups = firstTerminalBackups;
			}

			deliveryDefaults.terminalSupplier = {
				terminalId,
			};

			if (_.isEmpty(backups)) {
				deliveryDefaults.terminalSupplier.supplierId = supplierId;
			}

			const matchResult = matchProductToDelivery({
				stationDeliveryMap,
				stationInvoice,
				productType,
				netGallons: 0,
			});

			if (matchResult.expectedLine) {
				deliveryHelpText.netGallons = matchResult.expectedLine;
			} else if (!_.isEmpty(matchResult.reasonsForNotMatching)) {
				deliveryHelpText.netGallons = matchResult.reasonsForNotMatching.join('.');
			}
		}

		//- Fill in the default values. Either from the delivery or really default
		fields.forEach((field) => {
			const { name, defaultValue } = field;

			if (!_.isUndefined(deliveryDefaults[name])) {
				data[name] = deliveryDefaults[name];
			} else {
				data[name] = defaultValue;
			}

			if (!_.isUndefined(deliveryHelpText[name])) {
				field.helpText = deliveryHelpText[name];
			}

			if (field.productType) {
				field.productTypes = [productType];
			}
		});
		return { fields, data };
	};

	getValue = (path, defaultValue) => {
		const _d = _.isUndefined(defaultValue) ? '' : defaultValue;
		return _.get(this.state, path, _d);
	};
	getError = (path) => {
		return _.get(this.state.formFieldError, path);
	};

	render() {
		const { terminals, carriers, supplierMap } = this.props;
		const { fields, documentOptions, isBulk, isRack } = this.state;

		let justBulk = false;
		let noFilter = false;
		if (isBulk || isRack) {
			justBulk = true;
			noFilter = true;
		}

		return (
			<Fragment>
				<div>
					<FMPSelect
						label={`Document Type`}
						path={'documentOption'}
						onChange={this.onChange}
						value={this.getValue('documentOption')}
						error={this.getError('documentOption')}
						options={documentOptions}
						md={12}
					/>
				</div>
				<div>
					{!_.isEmpty(fields) && <FMPSectionHeader label={'Document Data'} />}

					{fields.map((field, i) => {
						const { label, name, type, helpText } = field;
						const path = `data.${name}`;
						if (type === 'string' || type === 'number') {
							return (
								<FMPText
									key={i}
									label={label}
									path={path}
									value={this.getValue(path)}
									error={this.getError(path)}
									onChange={this.onChange}
									helpText={helpText}
								/>
							);
						} else if (type === 'date') {
							return (
								<FMPDatePicker
									key={i}
									label={label}
									path={path}
									value={this.getValue(path)}
									error={this.getError(path)}
									onChange={this.onChange}
									helpText={helpText}
								/>
							);
						} else if (type === 'toggle') {
							return (
								<FMPSwitch
									key={i}
									label={label}
									value={this.getValue(path, false)}
									path={path}
									onChange={this.onChange}
								/>
							);
						} else if (type === 'terminalSupplierSelect') {
							const { productTypes, productDisplay } = field;

							const val = _.get(this.state, path, DEFAULT_TERMINAL_SUPPLIER_VALUE);

							return (
								<FMPTerminalSupplierSelect
									key={i}
									label={`${productDisplay} Terminal`}
									supplierLabel={`${productDisplay} Supplier`}
									error={this.getError(path)}
									supplierError={this.getError(path)}
									//- Value
									terminalId={val.terminalId}
									supplierId={val.supplierId}
									path={path}
									onChange={this.onTerminalSupplierChange}
									terminals={terminals}
									supplierMap={supplierMap}
									productTypes={productTypes}
									justBulk={justBulk}
									md={12}
								/>
							);
						} else if (type === 'terminalSelect') {
							const { productTypes } = field;

							return (
								<FMPTerminalSelect
									key={i}
									label={label}
									path={path}
									value={this.getValue(path)}
									error={this.getError(path)}
									onChange={this.onChange}
									terminals={terminals}
									productTypes={productTypes}
									noFilter={noFilter}
									md={12}
								/>
							);
						} else if (type === 'carrierSelect') {
							return (
								<FMPCarrierSelect
									key={i}
									label={label}
									path={path}
									value={this.getValue(path)}
									error={this.getError(path)}
									onChange={this.onChange}
									carriers={carriers}
								/>
							);
						} else {
							return <div key={i}>type : {type}</div>;
						}
					})}
				</div>
			</Fragment>
		);
	}
}

Page.propTypes = {
	documentType: PropTypes.string,
	stationDelivery: PropTypes.object,
	stationDeliveryMap: PropTypes.object,

	bulkDelivery: PropTypes.object,
	bulkDeliveryMap: PropTypes.object,

	data: PropTypes.object,
	formFieldError: PropTypes.object,

	terminals: PropTypes.array.isRequired,
	carriers: PropTypes.array.isRequired,
	supplierMap: PropTypes.object,
	onChange: PropTypes.func.isRequired,

	stationInvoice: PropTypes.object,
	bulkInvoice: PropTypes.object,
	rackTransfer: PropTypes.object,

	originalResourceId: PropTypes.number,

	showErrors: PropTypes.func,
	fields: PropTypes.array,
	classes: PropTypes.object.isRequired,
};
